import React, { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react';
import { Platform } from 'react-native';
import { useT } from '@transifex/react';

import { BIOMETRIC_FLAG } from '../config/runtimeConfig';
import { analytics } from '../utils/analytics';

import { BiometricEnrollmentModal } from './BiometricEnrollmentModal';
import { declineBiometricEnrollment, enrollBiometric } from './biometricEnrollment';
import { isBiometricEnrolled, wasBiometricDeclined } from './biometricStorage';
import { featureLabelFor } from './featureLabel';
import { useBiometricCapabilities } from './useBiometricCapabilities';

type PromptArgs = { accessToken: string; email: string };

type ContextValue = {
  // Show enrollment modal if eligible; resolves once the user decides,
  // immediately if not eligible. Safe to await before completeAuth().
  promptIfEligible: (args: PromptArgs) => Promise<void>;
};

const Ctx = createContext<ContextValue | null>(null);

export const useBiometricEnrollmentPrompt = (): ContextValue => {
  const value = useContext(Ctx);
  if (!value) {
    if (__DEV__ && Platform.OS !== 'web') {
      console.warn(
        '[biometric] useBiometricEnrollmentPrompt called outside BiometricEnrollmentProvider. ' +
          'Post-login enrollment will silently no-op. Ensure LoginRoot wraps the auth subtree.'
      );
    }
    // Consumer outside the provider (e.g. web) — no-op so screens don't guard.
    return {
      promptIfEligible: async () => {
        /* noop */
      },
    };
  }
  return value;
};

type ModalState =
  | { visible: false }
  | { visible: true; args: PromptArgs };

// Mounted by LoginRoot. Every auth-completing screen calls promptIfEligible()
// before completeAuth() — bail conditions short-circuit to an immediate
// resolve, otherwise the returned promise blocks on the user's decision.
export const BiometricEnrollmentProvider = ({ children }: { children: ReactNode }) => {
  const t = useT();
  const capabilities = useBiometricCapabilities();
  const [state, setState] = useState<ModalState>({ visible: false });
  const resolveRef = useRef<(() => void) | null>(null);

  const promptIfEligible = useCallback(
    async ({ accessToken, email }: PromptArgs): Promise<void> => {
      // Fail OPEN on any error — a broken SecureStore / offline PostHog must
      // never block the login handoff.
      try {
        if (Platform.OS === 'web') return;
        if (!capabilities.available || !capabilities.enrolled) return;
        // Skip on devices where the OS prompt would dead-end (e.g. Android with
        // fingerprint hardware but only weak-class face enrolled). Surfacing an
        // educational alert here would interrupt the just-completed login
        // handoff — the Settings screen owns that copy instead. Fire an
        // analytics event so the ME-1745 impact remains measurable: the exact
        // population this fix targets would otherwise vanish from the funnel.
        if (!capabilities.usable) {
          analytics.trackEvent('biometric_enrollment_skipped', {
            reason: 'class_mismatch',
            platform: Platform.OS,
            enrolled_level: capabilities.enrolledLevel,
          });
          return;
        }

        // Await the async lookup rather than reading a hoisted render-time
        // value: token-login captures `promptIfEligible` in a useEffect([])
        // and calls it there, so a stale first-render closure would freeze
        // the flag at its initial `false` value and enrollment would never
        // be offered on the magic-link path.
        const flagOn = await analytics.isFeatureFlagEnabled(BIOMETRIC_FLAG);
        if (!flagOn) return;

        if (await isBiometricEnrolled()) return;
        if (await wasBiometricDeclined()) return;

        analytics.trackEvent('biometric_enrollment_prompted');
        return await new Promise<void>((resolve) => {
          resolveRef.current = resolve;
          setState({ visible: true, args: { accessToken, email } });
        });
      } catch {
        /* swallow — skip prompt, let completeAuth continue */
      }
    },
    [
      capabilities.available,
      capabilities.enrolled,
      capabilities.usable,
      capabilities.enrolledLevel,
    ]
  );

  const onEnable = async () => {
    if (!state.visible) {
      resolveRef.current?.();
      resolveRef.current = null;
      return;
    }
    const args = state.args;
    const featureLabel = featureLabelFor(t, capabilities.type);
    // Close modal FIRST so LocalAuthentication's prompt (fired inside
    // enrollBiometric) isn't stacked behind our own overlay.
    setState({ visible: false });
    try {
      await enrollBiometric({
        ...args,
        promptMessage: t('Enable {feature} for Vida', { feature: featureLabel }),
        cancelLabel: t('Cancel'),
      });
    } finally {
      resolveRef.current?.();
      resolveRef.current = null;
    }
  };

  const onDecline = async () => {
    setState({ visible: false });
    try {
      await declineBiometricEnrollment();
    } finally {
      resolveRef.current?.();
      resolveRef.current = null;
    }
  };

  return (
    <Ctx.Provider value={{ promptIfEligible }}>
      {children}
      <BiometricEnrollmentModal
        visible={state.visible}
        biometricType={capabilities.type}
        onEnable={onEnable}
        onDecline={onDecline}
      />
    </Ctx.Provider>
  );
};
